import { useState } from 'react';

import HubRoundedIcon from '@mui/icons-material/HubRounded';
import SmartToyRoundedIcon from '@mui/icons-material/SmartToyRounded';
import { Stack } from '@mui/material';

import { testAiAgentProviderConnection } from '../../api/aiAgentSettingsApi';
import { useAiAgentSettings, useUpdateAiAgentSettings } from '../../hooks';
import { useDomainPermissions, useFeedback, useSettingsForm } from '../../lib';
import type {
  AiAgentType,
  AiAutonomyMode,
  UpdateAiAgentSettingsRequest,
} from '../../model/settingsTypes';
import {
  FeedbackSnackbar,
  FieldRow,
  MaskedCredentialField,
  MultiToggleField,
  SelectField,
  SettingsCard,
  SettingsScaffold,
  SwitchField,
  TestConnectionButton,
  TextInputField,
} from '../components';
import { AI_AGENT_TYPE_OPTIONS, AI_AUTONOMY_MODE_OPTIONS } from '../options';

/** AI agent settings: enabled assistants, autonomy and the model provider. */
export function AiAgentSettingsPage() {
  const { canView, canManage, canManageCredentials } = useDomainPermissions('ai-agents');
  const query = useAiAgentSettings();
  const update = useUpdateAiAgentSettings();
  const form = useSettingsForm(query.data);
  const [providerSecret, setProviderSecret] = useState('');
  const { feedback, success, error: notifyError, clear } = useFeedback();

  const dirty = form.dirty || providerSecret.trim() !== '';

  const reset = () => {
    form.reset();
    setProviderSecret('');
  };

  const handleSave = () => {
    const draft = form.draft;
    if (!draft) {
      return;
    }
    const body: UpdateAiAgentSettingsRequest = {
      version: draft.version,
      enabledAgents: draft.enabledAgents,
      autonomyMode: draft.autonomyMode,
      handoffToHumanEnabled: draft.handoffToHumanEnabled,
      workingHoursOnly: draft.workingHoursOnly,
      provider: {
        code: draft.provider?.code ?? null,
        model: draft.provider?.model ?? null,
        baseUrl: draft.provider?.baseUrl ?? null,
        ...(providerSecret.trim() !== '' ? { secret: providerSecret } : {}),
      },
    };
    update.mutate(body, {
      onSuccess: () => {
        setProviderSecret('');
        success('AI agent settings saved.');
      },
      onError: (error) => {
        notifyError(error.message);
        if (error.status === 409) {
          void query.refetch();
        }
      },
    });
  };

  return (
    <SettingsScaffold
      title="AI agent settings"
      subtitle="AI assistants, autonomy level and the model provider."
      icon={<SmartToyRoundedIcon />}
      query={query}
      canView={canView}
      canManage={canManage}
      audit={query.data}
      dirty={dirty}
      saving={update.isPending}
      onSave={handleSave}
      onReset={reset}
      feedback={<FeedbackSnackbar feedback={feedback} onClose={clear} />}
    >
      {() => {
        const draft = form.draft;
        if (!draft) {
          return null;
        }
        const disabled = !canManage;

        const setProvider = (patch: {
          code?: string | null;
          model?: string | null;
          baseUrl?: string | null;
        }) =>
          form.setField('provider', {
            code: draft.provider?.code ?? null,
            model: draft.provider?.model ?? null,
            baseUrl: draft.provider?.baseUrl ?? null,
            credential: draft.provider?.credential ?? null,
            ...patch,
          });

        return (
          <Stack spacing={2.5}>
            <SettingsCard title="Assistants" icon={<SmartToyRoundedIcon />}>
              <MultiToggleField
                label="Enabled agents"
                description="AI assistants available to your team."
                value={draft.enabledAgents}
                options={AI_AGENT_TYPE_OPTIONS}
                onChange={(value) => form.setField('enabledAgents', value as AiAgentType[])}
                disabled={disabled}
              />
              <SelectField
                label="Autonomy mode"
                value={draft.autonomyMode}
                options={AI_AUTONOMY_MODE_OPTIONS}
                onChange={(value) => form.setField('autonomyMode', value as AiAutonomyMode)}
                disabled={disabled}
              />
              <SwitchField
                label="Hand off to human"
                description="Escalate to a human when confidence is low."
                checked={draft.handoffToHumanEnabled}
                onChange={(value) => form.setField('handoffToHumanEnabled', value)}
                disabled={disabled}
              />
              <SwitchField
                label="Working hours only"
                checked={draft.workingHoursOnly}
                onChange={(value) => form.setField('workingHoursOnly', value)}
                disabled={disabled}
              />
            </SettingsCard>

            <SettingsCard
              title="Model provider"
              description="Configure and verify the LLM provider."
              icon={<HubRoundedIcon />}
            >
              <TextInputField
                label="Provider code"
                value={draft.provider?.code ?? ''}
                onChange={(value) => setProvider({ code: value || null })}
                disabled={disabled}
                placeholder="yandexgpt"
              />
              <TextInputField
                label="Model"
                value={draft.provider?.model ?? ''}
                onChange={(value) => setProvider({ model: value || null })}
                disabled={disabled}
                placeholder="yandexgpt-lite"
              />
              <TextInputField
                label="Base URL"
                type="url"
                value={draft.provider?.baseUrl ?? ''}
                onChange={(value) => setProvider({ baseUrl: value || null })}
                disabled={disabled}
                placeholder="https://llm.api.cloud.yandex.net"
              />
              <MaskedCredentialField
                label="API key"
                description="Secret used to authenticate with the model provider."
                credential={draft.provider?.credential ?? null}
                secret={providerSecret}
                onSecretChange={setProviderSecret}
                disabled={!canManageCredentials}
              />
              <FieldRow label="Connection" description="Verify the model provider is reachable." alignTop>
                <TestConnectionButton run={testAiAgentProviderConnection} disabled={!canManage} />
              </FieldRow>
            </SettingsCard>
          </Stack>
        );
      }}
    </SettingsScaffold>
  );
}
